import { ref } from 'vue'
import { githubApi } from '@/api/github'
import { createRequestGuard, withAsyncState } from '@/lib/asyncState'
import type { PaginationMeta, RuleFinding, RuleFindingFilters } from '@/types/models'

// Singleton, following useReviewRuns.ts's shape: shared state so the
// findings detail page retains its list/pagination across navigation within
// a session.
const ruleFindings = ref<RuleFinding[]>([])
const meta = ref<PaginationMeta | null>(null)
const loading = ref(false)
const error = ref<string | null>(null)

// Guards against a stale filter/page request's response landing after a
// newer one's and clobbering it.
const requestGuard = createRequestGuard()

export function useRuleFindings() {
  async function fetchRuleFindings(filters: RuleFindingFilters = {}): Promise<void> {
    const token = requestGuard.start()
    await withAsyncState(
      error,
      'Failed to load findings.',
      async () => {
        const page = await githubApi.listRuleFindings(filters)
        if (!requestGuard.isCurrent(token)) return
        ruleFindings.value = page.data
        meta.value = page.meta
      },
      loading,
      undefined,
      () => !requestGuard.isCurrent(token),
    )
  }

  // Called on logout so the next session's findings page doesn't briefly
  // show the previous user's data before its own fetch resolves, and so a
  // late response from a request the previous session started can't land
  // and repopulate it.
  function reset(): void {
    requestGuard.start()
    ruleFindings.value = []
    meta.value = null
    loading.value = false
    error.value = null
  }

  return {
    ruleFindings,
    meta,
    loading,
    error,
    fetchRuleFindings,
    reset,
  }
}
